import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import styles from './SearchSelect.module.css';

export interface SearchOption {
  value: string;
  label: string;
  /** Zweite Angabe rechts, z. B. Rufnummer; wird mit durchsucht. */
  hint?: string;
}

interface Base {
  label: string;
  options: SearchOption[];
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
}

interface Core extends Base {
  selected: string[];
  multiple: boolean;
  onPick: (value: string) => void;
  onRemove: (value: string) => void;
}

const norm = (s: string) => s.toLocaleLowerCase('de-DE');

function Combo({ label, options, placeholder, hint, disabled, className, selected, multiple, onPick, onRemove }: Core) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const shown = useMemo(() => {
    const q = norm(query.trim());
    return q ? options.filter((o) => norm(o.label).includes(q) || norm(o.hint ?? '').includes(q) || norm(o.value).includes(q)) : options;
  }, [options, query]);

  useEffect(() => setActive(0), [query, open]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [open]);

  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const single = !multiple ? selected[0] : undefined;
  const pick = (o: SearchOption) => {
    onPick(o.value);
    setQuery('');
    if (multiple) input.current?.focus();
    else setOpen(false);
  };

  const key = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(shown.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      const o = shown[active];
      if (o) pick(o);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Backspace' && multiple && !query && selected.length) onRemove(selected[selected.length - 1]!);
  };

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')} ref={root}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div className={styles.box} onClick={() => input.current?.focus()}>
        {multiple &&
          selected.map((v) => (
            <span key={v} className={styles.chip}>
              {labelOf(v)}
              <button type="button" onClick={() => onRemove(v)} aria-label={`${labelOf(v)} entfernen`} disabled={disabled}>
                ×
              </button>
            </span>
          ))}
        <input
          id={id}
          ref={input}
          className={styles.input}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={open || multiple ? query : single ? labelOf(single) : ''}
          placeholder={multiple ? (selected.length ? '' : (placeholder ?? 'Suchen …')) : (placeholder ?? 'Suchen …')}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={key}
        />
        {!multiple && single && !disabled && (
          <button type="button" className={styles.clear} onClick={() => onRemove(single)} aria-label="Auswahl entfernen">
            ×
          </button>
        )}
      </div>
      {open && (
        <ul id={`${id}-list`} className={styles.list} role="listbox" aria-label={label}>
          {shown.length === 0 && <li className={styles.empty}>Nichts gefunden.</li>}
          {shown.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={selected.includes(o.value)}
              className={`${styles.option} ${i === active ? styles.active : ''}`}
              onPointerEnter={() => setActive(i)}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              <span>{o.label}</span>
              {o.hint && <span className={styles.sub}>{o.hint}</span>}
            </li>
          ))}
        </ul>
      )}
      {hint ? <p className={styles.hint}>{hint}</p> : null}
    </div>
  );
}

/** Auswahl mit Suchfeld: tippen filtert die Liste, Pfeiltasten und Enter wählen. */
export function SearchSelect(props: Base & { value: string; onChange: (value: string) => void }) {
  const { value, onChange, ...rest } = props;
  return <Combo {...rest} selected={value ? [value] : []} multiple={false} onPick={onChange} onRemove={() => onChange('')} />;
}

/** Mehrfachauswahl mit Suchfeld; gewählte Einträge erscheinen als Chips. */
export function MultiSearchSelect(props: Base & { values: string[]; onChange: (values: string[]) => void }) {
  const { values, onChange, ...rest } = props;
  return <Combo {...rest} selected={values} multiple onPick={(v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v])} onRemove={(v) => onChange(values.filter((x) => x !== v))} />;
}
